// src/components/AdminEntitlements.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Search } from 'lucide-react';
import { useOperatorClaims } from '../hooks/useOperatorClaims';
import {
  fetchEntitlementAudit,
  fetchEntitlementList,
  fetchEntitlementLookup,
  operatorErrorMessage,
  postEntitlementOverride,
} from '../utils/adminEntitlements';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'has_credit', label: 'Has credit' },
  { id: 'consumed', label: 'Consumed' },
  { id: 'comp', label: 'Comp override' },
  { id: 'paid', label: 'Paid / stub' },
];

function formatTimestamp(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function evaluationLabel(evaluation) {
  if (!evaluation) return 'Unknown';
  if (evaluation.allowed) {
    const reason = evaluation.reason || 'allowed';
    if (reason === 'free_pass') return 'Would allow (complimentary)';
    if (reason === 'override') return 'Would allow (comp)';
    if (reason === 'paid') return 'Would allow (paid/stub)';
    return 'Would allow';
  }
  const code = evaluation.denial?.code || 'denied';
  if (code === 'FEATURE_DISABLED') return 'Blocked (kill switch)';
  return 'Would deny';
}

function EvaluationBanner({ evaluation, featureEnabled }) {
  const allowed = evaluation?.allowed === true;
  const killSwitch = evaluation?.denial?.code === 'FEATURE_DISABLED' || featureEnabled === false;
  const base =
    'rounded-md px-3 py-2 text-sm border ' +
    (allowed
      ? 'bg-green-50 border-green-200 text-green-900 dark:bg-green-950/40 dark:border-green-800 dark:text-green-100'
      : killSwitch
        ? 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-100'
        : 'bg-red-50 border-red-200 text-red-900 dark:bg-red-950/40 dark:border-red-800 dark:text-red-100');

  return (
    <div className={base}>
      <strong>{evaluationLabel(evaluation)}</strong>
      {evaluation?.denial?.message && (
        <p className="mt-1 text-xs opacity-90">{evaluation.denial.message}</p>
      )}
    </div>
  );
}

function AdminEntitlements() {
  const { isSuperAdmin } = useOperatorClaims();

  const [filter, setFilter] = useState('all');
  const [listItems, setListItems] = useState([]);
  const [listCursor, setListCursor] = useState(null);
  const [cursorStack, setCursorStack] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');

  const [searchMode, setSearchMode] = useState('email');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUid, setSelectedUid] = useState(null);
  const [detail, setDetail] = useState(null);
  const [auditItems, setAuditItems] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const [grantExpiry, setGrantExpiry] = useState('');
  const [grantNote, setGrantNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState('');

  const loadList = useCallback(
    async (cursor = null, pushCursor = false) => {
      setListLoading(true);
      setListError('');
      try {
        const data = await fetchEntitlementList({ filter, limit: 25, cursor });
        setListItems(data.items || []);
        if (pushCursor && cursor) {
          setCursorStack((prev) => [...prev, cursor]);
        }
        setListCursor(data.nextCursor || null);
      } catch (err) {
        setListError(operatorErrorMessage(err));
        setListItems([]);
      } finally {
        setListLoading(false);
      }
    },
    [filter]
  );

  const loadDetail = useCallback(async (targetUid) => {
    if (!targetUid) return;
    setSelectedUid(targetUid);
    setDetailLoading(true);
    setDetailError('');
    setActionMessage('');
    try {
      const [lookup, audit] = await Promise.all([
        fetchEntitlementLookup({ targetUid }),
        fetchEntitlementAudit({ targetUid, limit: 50 }),
      ]);
      setDetail(lookup);
      setAuditItems(audit.items || []);
    } catch (err) {
      setDetailError(operatorErrorMessage(err));
      setDetail(null);
      setAuditItems([]);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    setCursorStack([]);
    loadList(null, false);
  }, [filter, loadList]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setDetailLoading(true);
    setDetailError('');
    try {
      const lookup = await fetchEntitlementLookup(
        searchMode === 'email' ? { email: q } : { targetUid: q }
      );
      setDetail(lookup);
      setSelectedUid(lookup.targetUid);
      const audit = await fetchEntitlementAudit({ targetUid: lookup.targetUid, limit: 50 });
      setAuditItems(audit.items || []);
    } catch (err) {
      setDetailError(operatorErrorMessage(err));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleGrant = async () => {
    if (!selectedUid) return;
    setActionLoading(true);
    setActionMessage('');
    try {
      const body = {
        action: 'grant',
        targetUid: selectedUid,
        overrideKind: 'comp',
      };
      if (grantExpiry) {
        body.overrideExpiresAt = new Date(grantExpiry).toISOString();
      }
      if (grantNote.trim()) body.note = grantNote.trim();
      const result = await postEntitlementOverride(body);
      setActionMessage(`Comp granted (audit ${result.auditId}).`);
      await loadDetail(selectedUid);
      await loadList(cursorStack[cursorStack.length - 1] || null, false);
    } catch (err) {
      setActionMessage(operatorErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!selectedUid) return;
    if (!window.confirm('Revoke comp override for this user?')) return;
    setActionLoading(true);
    setActionMessage('');
    try {
      const result = await postEntitlementOverride({
        action: 'revoke',
        targetUid: selectedUid,
      });
      setActionMessage(`Override revoked (audit ${result.auditId}).`);
      await loadDetail(selectedUid);
    } catch (err) {
      setActionMessage(operatorErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const summary = detail?.summary;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Entitlements (operator)</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Read-only reporting for support; comp grant/revoke requires super_admin.
        </p>
      </div>

      <form onSubmit={handleSearch} className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Search by</label>
          <select
            value={searchMode}
            onChange={(e) => setSearchMode(e.target.value)}
            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-2 text-sm"
          >
            <option value="email">Email</option>
            <option value="uid">UID</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Query</label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchMode === 'email' ? 'user@example.com' : 'Firebase UID'}
            className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-indigo-600 text-white text-sm hover:bg-indigo-700"
        >
          <Search className="w-4 h-4" />
          Search
        </button>
      </form>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={
                  'px-2 py-1 text-xs rounded-md border ' +
                  (filter === f.id
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300')
                }
              >
                {f.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => loadList(cursorStack[cursorStack.length - 1] || null, false)}
              className="ml-auto inline-flex items-center gap-1 text-xs text-indigo-600"
            >
              <RefreshCw className="w-3 h-3" />
              Refresh
            </button>
          </div>

          {listError && <p className="text-sm text-red-600 mb-2">{listError}</p>}
          {listLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-gray-500">
                    <th className="py-2 pr-2">UID</th>
                    <th className="py-2 pr-2">Credits</th>
                    <th className="py-2">Evaluation</th>
                  </tr>
                </thead>
                <tbody>
                  {listItems.map((row) => (
                    <tr
                      key={row.targetUid}
                      className={
                        'border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ' +
                        (selectedUid === row.targetUid ? 'bg-indigo-50 dark:bg-indigo-950/30' : '')
                      }
                      onClick={() => loadDetail(row.targetUid)}
                    >
                      <td className="py-2 pr-2 font-mono text-xs truncate max-w-[140px]">
                        {row.targetUid}
                      </td>
                      <td className="py-2 pr-2">{row.summary?.freePassCreditsRemaining ?? '—'}</td>
                      <td className="py-2 text-xs">{evaluationLabel(row.evaluation)}</td>
                    </tr>
                  ))}
                  {listItems.length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-4 text-gray-500 text-center">
                        No rows for this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-2 mt-3">
            <button
              type="button"
              disabled={cursorStack.length === 0 || listLoading}
              onClick={() => {
                const next = [...cursorStack];
                next.pop();
                const prev = next[next.length - 1] || null;
                setCursorStack(next);
                loadList(prev, false);
              }}
              className="px-2 py-1 text-xs rounded border disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!listCursor || listLoading}
              onClick={() => loadList(listCursor, true)}
              className="px-2 py-1 text-xs rounded border disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-4">
          {detailLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          ) : detailError ? (
            <p className="text-sm text-red-600">{detailError}</p>
          ) : detail ? (
            <>
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">User detail</h2>
                <p className="font-mono text-xs text-gray-600 dark:text-gray-400 mt-1 break-all">
                  {detail.targetUid}
                </p>
              </div>

              <EvaluationBanner evaluation={detail.evaluation} featureEnabled={detail.featureEnabled} />

              {detail.authHints && (
                <div className="text-sm space-y-1">
                  <h3 className="font-medium text-gray-800 dark:text-gray-200">Auth hints (US-11)</h3>
                  <p>Email verified: {detail.authHints.emailVerified ? 'Yes' : 'No'}</p>
                  {detail.authHints.email && <p>Email: {detail.authHints.email}</p>}
                  <p>
                    Identity exhausted: {detail.authHints.identityExhausted ? 'Yes' : 'No'}
                  </p>
                </div>
              )}

              {summary ? (
                <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                  <dt className="text-gray-500">Credits</dt>
                  <dd>{summary.freePassCreditsRemaining ?? '—'}</dd>
                  <dt className="text-gray-500">Consumed at</dt>
                  <dd>{formatTimestamp(summary.freePassConsumedAt)}</dd>
                  <dt className="text-gray-500">Override</dt>
                  <dd>{summary.overrideKind || 'none'}</dd>
                  <dt className="text-gray-500">Override expires</dt>
                  <dd>{formatTimestamp(summary.overrideExpiresAt)}</dd>
                  <dt className="text-gray-500">Billing status</dt>
                  <dd>{summary.billingStatus || 'none'}</dd>
                  <dt className="text-gray-500">Paid credits</dt>
                  <dd>{summary.paidCreditsRemaining ?? '—'}</dd>
                  <dt className="text-gray-500">Plan ID</dt>
                  <dd className="break-all">{summary.billingPlanId || '—'}</dd>
                </dl>
              ) : (
                <p className="text-sm text-gray-600">No entitlement summary document.</p>
              )}

              {isSuperAdmin && selectedUid && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-2">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100">Comp override</h3>
                  <label className="block text-xs text-gray-500">
                    Expiry (optional)
                    <input
                      type="datetime-local"
                      value={grantExpiry}
                      onChange={(e) => setGrantExpiry(e.target.value)}
                      className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
                    />
                  </label>
                  <label className="block text-xs text-gray-500">
                    Audit note (optional)
                    <textarea
                      value={grantNote}
                      onChange={(e) => setGrantNote(e.target.value)}
                      rows={2}
                      className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
                    />
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={handleGrant}
                      className="px-3 py-1.5 text-sm rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      Grant comp
                    </button>
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={handleRevoke}
                      className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Revoke
                    </button>
                  </div>
                  {actionMessage && (
                    <p className="text-xs text-gray-700 dark:text-gray-300">{actionMessage}</p>
                  )}
                </div>
              )}

              <div>
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Audit log</h3>
                {auditItems.length === 0 ? (
                  <p className="text-sm text-gray-500">No audit rows for this user.</p>
                ) : (
                  <ul className="space-y-2 text-xs max-h-48 overflow-y-auto">
                    {auditItems.map((row) => (
                      <li
                        key={row.id}
                        className="border border-gray-100 dark:border-gray-700 rounded p-2"
                      >
                        <div className="font-medium">
                          {row.action} · {formatTimestamp(row.timestamp)}
                        </div>
                        <div className="text-gray-500">by {row.actorUid}</div>
                        {row.payload && (
                          <pre className="mt-1 whitespace-pre-wrap break-all text-gray-600 dark:text-gray-400">
                            {JSON.stringify(row.payload)}
                          </pre>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">Select a list row or search to view detail.</p>
          )}
        </section>
      </div>
    </div>
  );
}

export default AdminEntitlements;
